import {PermissionType} from '@hconnect/apiclient'
import {areaDepartmentCodes, kpiAreaCodes} from '@hconnect/common/consts'
import {PlantStructureTabs} from '@hconnect/common/enums'
import {TreeNode, UpmEntityType} from '@hconnect/common/types'

import {departmentsWithConfig} from '../consts'
import {
  isAssetEnabledForPlanning,
  isDepartmentOrEquipmentNode,
  isStorageEnabledForPlanning
} from '../helpers'

const CRUSHING_AREA_CODE = '20'

export const upmNodeToTabsMap: Record<UpmEntityType, PlantStructureTabs[]> = {
  [UpmEntityType.Plant]: [
    PlantStructureTabs.General,
    PlantStructureTabs.Configuration,
    PlantStructureTabs.GrindingCircuits,
    PlantStructureTabs.Sensors,
    PlantStructureTabs.Kpis,
    PlantStructureTabs.BaseLoad
  ],
  [UpmEntityType.Area]: [
    PlantStructureTabs.Sensors,
    PlantStructureTabs.CrusherFlow,
    PlantStructureTabs.GrindingCircuits,
    PlantStructureTabs.Kpis
  ],
  [UpmEntityType.ProductionLine]: [PlantStructureTabs.Sensors],
  [UpmEntityType.Department]: Object.values(PlantStructureTabs).filter(
    (tab) => tab !== PlantStructureTabs.BaseLoad
  ),
  [UpmEntityType.Equipment]: Object.values(PlantStructureTabs).filter(
    (tab) => tab !== PlantStructureTabs.BaseLoad
  )
}

export const tabToPermissionsMap: Record<PlantStructureTabs, PermissionType[]> = {
  [PlantStructureTabs.General]: [],
  [PlantStructureTabs.Configuration]: ['VIEW_MATERIALS', 'CREATE_MASTER_DATA'],
  [PlantStructureTabs.CrusherFlow]: [],
  [PlantStructureTabs.Layout]: ['CREATE_MASTER_DATA'],
  [PlantStructureTabs.HeatZones]: ['CREATE_MASTER_DATA'],
  [PlantStructureTabs.DrillingZones]: ['CREATE_MASTER_DATA'],
  [PlantStructureTabs.Sensors]: [],
  [PlantStructureTabs.Kpis]: [],
  [PlantStructureTabs.GrindingCircuits]: [],
  [PlantStructureTabs.BaseLoad]: [],
  [PlantStructureTabs.OperationModes]: ['VIEW_MACHINE_PLAN'],
  [PlantStructureTabs.StandardTimes]: ['VIEW_MACHINE_PLAN'],
  [PlantStructureTabs.Optimizer]: ['VIEW_MACHINE_PLAN'],
  [PlantStructureTabs.Connection]: ['VIEW_MATERIAL_STORAGE'],
  [PlantStructureTabs.Admin]: []
}

export const tabToUserIdsAllowed: Record<PlantStructureTabs, (userId: string) => boolean> = {
  [PlantStructureTabs.General]: () => true,
  [PlantStructureTabs.Configuration]: () => true,
  [PlantStructureTabs.CrusherFlow]: () => true,
  [PlantStructureTabs.Layout]: () => true,
  [PlantStructureTabs.HeatZones]: () => true,
  [PlantStructureTabs.DrillingZones]: () => true,
  [PlantStructureTabs.Sensors]: () => true,
  [PlantStructureTabs.Kpis]: () => true,
  [PlantStructureTabs.BaseLoad]: () => true,
  [PlantStructureTabs.GrindingCircuits]: () => true,
  [PlantStructureTabs.OperationModes]: () => true,
  [PlantStructureTabs.StandardTimes]: () => true,
  [PlantStructureTabs.Optimizer]: () => true,
  [PlantStructureTabs.Connection]: () => true,
  [PlantStructureTabs.Admin]: (userId) =>
    [
      'd823e6ac-30e3-4ce6-88b5-b328dcbda631',
      'ba1100d7-d746-4319-989d-cf1f01cbefbd',
      '65b71d71-81f7-44fd-b57f-05a8c4ee21f9',
      'e79f8b9b-be9a-49fd-806d-2222c9df7f14',
      '40c85dc6-5630-4afb-bcce-a1c6a0edeeba',
      'ce453d60-449e-4583-9a28-f43623cd449b',
      'dc68a11f-7859-4aab-a7f3-91b4bdbe63a0',
      '15ce7263-186c-4024-b160-cbbfcb0f60d8',
      'cee3f723-bada-440d-90e1-3e57e85389cb',
      'cd4b678e-ae0b-4193-8938-d28a84bc1a37',
      '5816b3c4-592f-460e-8c17-d58e3947e8ed',
      '86f532f3-66d0-4545-88da-510ec1ad70e6',
      '6296fa10-24f2-4528-bfbd-468d08cb1d2d',
      '4767a8df-a149-41b0-8784-73c01b586a8e'
    ].includes(userId)
}

export const tabToCustomFilterMap: Record<PlantStructureTabs, (selectedItem: TreeNode) => boolean> =
  {
    [PlantStructureTabs.General]: () => true,
    [PlantStructureTabs.Configuration]: (selectedItem) => {
      return (
        selectedItem.itemType === UpmEntityType.Plant ||
        (selectedItem.itemType === UpmEntityType.Department &&
          departmentsWithConfig.some((department) => selectedItem.entityCodesPath === department))
      )
    },
    [PlantStructureTabs.CrusherFlow]: (selectedItem) =>
      selectedItem.itemType === UpmEntityType.Area &&
      selectedItem.type?.code === CRUSHING_AREA_CODE,
    [PlantStructureTabs.Layout]: (selectedItem) => selectedItem.type?.name === 'Kiln',
    [PlantStructureTabs.HeatZones]: (selectedItem) => selectedItem.type?.name === 'Kiln',
    [PlantStructureTabs.DrillingZones]: (selectedItem) => selectedItem.type?.name === 'Kiln',
    [PlantStructureTabs.Sensors]: () => true,
    [PlantStructureTabs.Kpis]: (selectedItem) =>
      selectedItem.itemType === UpmEntityType.Plant ||
      (selectedItem.itemType === UpmEntityType.Department &&
        areaDepartmentCodes.includes(selectedItem.entityCodesPath ?? '')) ||
      (selectedItem.itemType === UpmEntityType.Area &&
        kpiAreaCodes.includes(selectedItem.type?.code ?? '')),

    [PlantStructureTabs.BaseLoad]: () => true,
    [PlantStructureTabs.GrindingCircuits]: (selectedItem) =>
      selectedItem.itemType === UpmEntityType.Plant ||
      (selectedItem.itemType === UpmEntityType.Area && selectedItem.type?.code === '50'), // Cement Grinding
    [PlantStructureTabs.OperationModes]: (selectedItem) =>
      isDepartmentOrEquipmentNode(selectedItem) &&
      isAssetEnabledForPlanning(selectedItem.enabledForPlanningItem),
    [PlantStructureTabs.StandardTimes]: (selectedItem) =>
      isDepartmentOrEquipmentNode(selectedItem) &&
      isAssetEnabledForPlanning(selectedItem.enabledForPlanningItem),
    [PlantStructureTabs.Optimizer]: (selectedItem) =>
      isDepartmentOrEquipmentNode(selectedItem) &&
      isAssetEnabledForPlanning(selectedItem.enabledForPlanningItem),
    [PlantStructureTabs.Connection]: (selectedItem) =>
      isDepartmentOrEquipmentNode(selectedItem) &&
      isStorageEnabledForPlanning(selectedItem.enabledForPlanningItem),
    [PlantStructureTabs.Admin]: (selectedItem) => selectedItem.itemType === UpmEntityType.Department
  }
